import { buildFleetTimelineData } from "./routeModelsData.js";
import { buildFleetSequenceDisplayItems, buildSequenceTableRenderItems, resolveDisplayedOperationalSequenceLabel } from "./fleetTimelinePresentation.js";

// These labels describe the retained Dispatch, never the editable billing order.
export function dispatchStepLabels(assignment, dispatchDate, dispatchPeriod) {
  const labels = new Map();
  if (!assignment) return labels;
  const steps = assignment.operational_steps?.length ? assignment.operational_steps : (assignment.trips || []).flatMap((trip) =>
    (trip.operational_steps || []).map((step) => ({ ...step, trip_id: step.trip_id || trip.trip_id })));
  const row = buildFleetTimelineData({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod }, [assignment]).rows[0];
  const register = (segment, label) => {
    if (!segment) return;
    if (segment.mergedMemberSegments?.length) {
      segment.mergedMemberSegments.forEach((member) => register(member, label));
      return;
    }
    let stepId = segment.businessStep?.stepId;
    if (!stepId && segment.isInitialPreload) {
      // Initial preloads have no businessStep in the Dispatch renderer. Only
      // associate an unambiguous source step, never another visit to that site.
      const matches = steps.filter((step) => step.trip_id === segment.tripId
        && step.step_type === "loading" && step.order_id === segment.operationOrderId);
      if (matches.length === 1) stepId = matches[0].step_id;
    }
    if (stepId && label) labels.set(String(stepId), label);
  };
  for (const item of buildSequenceTableRenderItems(buildFleetSequenceDisplayItems(row?.segments || []))) {
    if (item.kind === "group") item.memberSegments.forEach((segment, index) => register(segment, String(item.startSequence + index)));
    else if (item.kind === "segment") register(item.segment, resolveDisplayedOperationalSequenceLabel(item.segment));
  }
  return labels;
}

// Planned incoming travel is display-only: never synthesize a real arrival.
export function dispatchPlannedTravel(assignment, dispatchDate, dispatchPeriod) {
  const travel = new Map();
  if (!assignment || !dispatchDate) return travel;
  const row = buildFleetTimelineData({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod }, [assignment]).rows[0];
  if (row?.timelineRenderState?.status && row.timelineRenderState.status !== "ready") return travel;
  for (const segment of row?.segments || []) {
    const id = segment.businessStep?.stepId;
    if (!id || !["loading", "delivery"].includes(segment.stopType)) continue;
    const minutes = segment.incomingTravelDurationMinutes;
    const valid = Number.isFinite(minutes) && minutes >= 0 && segment.tripId
      && Number.isInteger(segment.incomingRouteSegmentIndex);
    // An ambiguous or missing source stays unavailable, including in grouped visits.
    travel.set(String(id), !travel.has(String(id)) && valid ? {
      key: JSON.stringify([segment.tripId, segment.incomingRouteSegmentIndex]), seconds: minutes * 60,
    } : null);
  }
  return travel;
}

export function visitPlannedTravelSeconds(visit, travel) {
  const legs = (visit.source_step_ids || []).map((id) => travel.get(String(id)));
  if (!legs.length || legs.some((leg) => !leg)) return null;
  return [...new Map(legs.map((leg) => [leg.key, leg.seconds])).values()].reduce((total, seconds) => total + seconds, 0);
}

export function visitDispatchLabel(visit, labels) {
  const ids = visit.source_step_ids || [];
  if (!ids.length || ids.some((id) => !labels.has(id))) return "—";
  const numbers = [...new Set(ids.flatMap((id) => {
    const [start, end] = labels.get(id).split(/[-–]/).map(Number);
    return Array.from({ length: (end || start) - start + 1 }, (_, index) => start + index);
  }))].sort((a, b) => a - b);
  const ranges = [];
  for (let index = 0; index < numbers.length; index += 1) {
    const start = numbers[index];
    while (numbers[index + 1] === numbers[index] + 1) index += 1;
    ranges.push(start === numbers[index] ? String(start) : `${start}–${numbers[index]}`);
  }
  return ranges.join(", ");
}

export function dispatchInspectionLabel(assignment, dispatchDate, dispatchPeriod) {
  if (!assignment) return "";
  const row = buildFleetTimelineData({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod }, [assignment]).rows[0];
  const inspections = buildSequenceTableRenderItems(buildFleetSequenceDisplayItems(row?.segments || []))
    .flatMap((item) => item.kind === "group" ? item.memberSegments : item.kind === "segment" ? [item.segment] : [])
    .filter((segment) => segment.stopType === "depot_start" && !segment.isTerminalRouteMarker);
  return inspections.length === 1 ? resolveDisplayedOperationalSequenceLabel(inspections[0]) : "";
}

export function routeStopCounts(routes) {
  const visits = routes.flatMap((route) => route.sheet.visits).filter((visit) => visit.status !== "inherited");
  const groups = visits.filter((visit) => visit.orders.length > 1).length;
  return `${visits.length} arrêt${visits.length === 1 ? "" : "s"} · ${groups} groupe${groups === 1 ? "" : "s"}`;
}
